import { RiErrorWarningLine } from "@remixicon/react";
import { Alert } from "@screener/uikit/components/alert";
import { Button } from "@screener/uikit/components/button";
import { Field, FieldLabel } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import { type FormEvent, useCallback, useState } from "react";
import { type RequestPasswordResetResult, requestPasswordReset } from "@/lib/auth-api";
import { describeFailure } from "@/login-form";
import { AuthHeading, AuthLayout } from "./auth-layout";

/** Состояние экрана запроса: покой, ожидание ответа демона или его исход. */
export type RequestState = "idle" | "pending" | RequestPasswordResetResult;

/** Общий ответ на `202`: не утверждает, что почта есть и письмо ушло (§129 п. 2). */
const ACCEPTED_MESSAGE = "Если почта зарегистрирована, письмо отправлено";

const DEFAULT_BACK_LABEL = "Вернуться ко входу";
const RATE_LIMITED_MESSAGE = "Слишком много запросов. Подождите и попробуйте снова.";

/**
 * Текст ответа. Сбой говорит теми же словами, что форма входа; у `429` свой
 * текст — про запросы, а не про попытки входа.
 * `switch` с `satisfies never`: новый исход не уйдёт к человеку без сообщения.
 */
export function describeRequest(state: RequestState): string | null {
  switch (state) {
    case "idle":
    case "pending":
      return null;
    case "accepted":
      return ACCEPTED_MESSAGE;
    case "rate-limited":
      return RATE_LIMITED_MESSAGE;
    case "unavailable":
      return describeFailure(state);
    default:
      return state satisfies never;
  }
}

/**
 * Можно ли отправлять: нельзя, пока предыдущий запрос без ответа (двойная
 * отправка), и после общего ответа (форма уступает место сообщению).
 */
export function canSubmitRequest(state: RequestState): boolean {
  return state !== "pending" && state !== "accepted";
}

/** Отправитель — параметр, чтобы проверять без сети. */
export function submitRequest(
  email: string,
  send: (email: string) => Promise<RequestPasswordResetResult> = requestPasswordReset,
): Promise<RequestPasswordResetResult> {
  return send(email.trim());
}

interface PasswordResetRequestViewProps {
  email: string;
  state: RequestState;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  /** Подпись возврата: вошедшему — не «ко входу». */
  backLabel?: string;
}

/** Разметка экрана запроса сброса: форма с почтой либо общий ответ. */
export function PasswordResetRequestView({
  email,
  state,
  onEmailChange,
  onSubmit,
  onBack,
  backLabel = DEFAULT_BACK_LABEL,
}: PasswordResetRequestViewProps) {
  const message = describeRequest(state);
  const accepted = state === "accepted";
  const pending = state === "pending";

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <AuthLayout>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-6"
        aria-label="Сброс пароля"
        noValidate
      >
        <AuthHeading title="Сброс пароля">
          {accepted ? null : (
            <p className="text-[13px] leading-normal text-muted-foreground">
              Укажите почту учётной записи, мы отправим ссылку для смены пароля
            </p>
          )}
        </AuthHeading>
        {message !== null && !accepted ? (
          <Alert variant="error" size="sm">
            <RiErrorWarningLine aria-hidden="true" />
            <span>{message}</span>
          </Alert>
        ) : null}
        {/* Регион в DOM всегда: скринридер объявляет только изменение текста в уже существующем. */}
        <p role="status" className="text-sm text-muted-foreground empty:hidden">
          {accepted ? message : null}
        </p>
        {accepted ? null : (
          <>
            <Field>
              <FieldLabel className="text-xs">Почта</FieldLabel>
              <Input
                size="lg"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => onEmailChange(event.target.value)}
                disabled={pending}
                required
              />
            </Field>
            <Button type="submit" size="lg" className="w-full" disabled={pending}>
              {pending ? "Отправка…" : "Отправить"}
            </Button>
          </>
        )}
        <Button type="button" variant="link" size="sm" onClick={onBack}>
          {backLabel}
        </Button>
      </form>
    </AuthLayout>
  );
}

interface PasswordResetRequestScreenProps {
  /** «Вернуться ко входу». */
  onBack: () => void;
  /** Подпись возврата; по умолчанию «Вернуться ко входу». */
  backLabel?: string;
  /** Отправитель — параметр, чтобы проверять без сети. */
  requestSender?: (email: string) => Promise<RequestPasswordResetResult>;
}

/**
 * Экран запроса сброса пароля (`#/forgot`, stage9-password-reset, Р6). Почта
 * держится только в состоянии экрана и уходит одним запросом.
 */
export function PasswordResetRequestScreen({
  onBack,
  backLabel,
  requestSender = requestPasswordReset,
}: PasswordResetRequestScreenProps) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<RequestState>("idle");

  const handleSubmit = useCallback(() => {
    if (!canSubmitRequest(state) || email.trim() === "") {
      return;
    }
    setState("pending");
    submitRequest(email, requestSender).then(setState);
  }, [email, state, requestSender]);

  return (
    <PasswordResetRequestView
      email={email}
      state={state}
      onEmailChange={setEmail}
      onSubmit={handleSubmit}
      onBack={onBack}
      backLabel={backLabel}
    />
  );
}
